import { PollEventTypeCode, PollType } from '../model/IPoll';
import { PollQuestionType, PollState } from '../services/DataService';

/**
 * Human-readable labels for the enums that come straight from the database.
 * Kept in one place so the list, the details page, the forms and the export
 * all agree on wording.
 */

export type BadgeColor =
    | 'brand'
    | 'danger'
    | 'important'
    | 'informative'
    | 'severe'
    | 'subtle'
    | 'success'
    | 'warning';

export interface IStateMeta {
    label: string;
    color: BadgeColor;
}

export const POLL_STATE_META: Record<PollState, IStateMeta> = {
    [PollState.Draft]: { label: 'Черновик', color: 'subtle' },
    [PollState.Review]: { label: 'На проверке', color: 'informative' },
    [PollState.Rejected]: { label: 'Отклонён', color: 'danger' },
    [PollState.Approved]: { label: 'Утверждён', color: 'brand' },
    [PollState.Scheduled]: { label: 'Запланирован', color: 'informative' },
    [PollState.Active]: { label: 'Активен', color: 'success' },
    [PollState.Paused]: { label: 'Приостановлен', color: 'warning' },
    [PollState.Closed]: { label: 'Завершён', color: 'important' },
    [PollState.Archived]: { label: 'В архиве', color: 'subtle' },
    [PollState.Deleted]: { label: 'Удалён', color: 'danger' },
};

export function getStateMeta(state?: PollState | number | null): IStateMeta {
    if (state === undefined || state === null) {
        return { label: 'Без статуса', color: 'subtle' };
    }
    return POLL_STATE_META[state as PollState] ?? { label: `Статус ${state}`, color: 'subtle' };
}

export const POLL_STATE_OPTIONS: { value: PollState; label: string }[] = (
    Object.keys(POLL_STATE_META).map(Number) as PollState[]
).map((value) => ({ value, label: POLL_STATE_META[value].label }));

export const EVENT_TYPE_OPTIONS: { value: PollEventTypeCode; label: string }[] = [
    { value: PollEventTypeCode.Lecture, label: 'Лекция / презентация' },
    { value: PollEventTypeCode.RoundTable, label: 'Круглый стол' },
    { value: PollEventTypeCode.Conference, label: 'Конгресс / конференция' },
    { value: PollEventTypeCode.BayerConference, label: 'Конференция Bayer' },
    { value: PollEventTypeCode.ForeignCongress, label: 'Зарубежный конгресс' },
];

export function getEventTypeLabel(code?: PollEventTypeCode | number | null): string {
    if (code === undefined || code === null) return '—';
    return EVENT_TYPE_OPTIONS.find((x) => x.value === code)?.label ?? `Тип ${code}`;
}

export interface IQuestionTypeMeta {
    value: PollQuestionType;
    label: string;
    description: string;
}

export const QUESTION_TYPE_OPTIONS: IQuestionTypeMeta[] = [
    { value: PollQuestionType.Rating, label: 'Рейтинг', description: 'Оценка по шкале' },
    { value: PollQuestionType.SingleChoice, label: 'Один вариант', description: 'Выбор одного ответа из списка' },
    { value: PollQuestionType.MultipleChoice, label: 'Несколько вариантов', description: 'Выбор нескольких ответов' },
    { value: PollQuestionType.Text, label: 'Открытый текст', description: 'Свободный ответ' },
    { value: PollQuestionType.Nps, label: 'NPS', description: 'Индекс лояльности 0–10' },
    { value: PollQuestionType.WordCloud, label: 'Облако слов', description: 'Ключевые слова и фразы' },
];

/**
 * Положение логотипа на экране респондента. Коды совпадают с перечислением
 * ScreenPosition в бэкенде (колонка polls.branding_logo_position_code).
 */
export const LOGO_POSITION_TOP_LEFT = 0;

export const LOGO_POSITION_OPTIONS: { code: number; label: string }[] = [
    { code: LOGO_POSITION_TOP_LEFT, label: 'Слева сверху' },
    { code: 1, label: 'По центру сверху' },
    { code: 2, label: 'Справа сверху' },
    { code: 3, label: 'Слева снизу' },
    { code: 4, label: 'Справа снизу' },
];

export function getLogoPositionLabel(code?: number | null): string {
    return LOGO_POSITION_OPTIONS.find((p) => p.code === code)?.label ?? '—';
}

export function getQuestionTypeLabel(type?: PollQuestionType | number | null): string {
    if (type === undefined || type === null) return 'Вопрос';
    return QUESTION_TYPE_OPTIONS.find((x) => x.value === type)?.label ?? 'Вопрос';
}

export function getPollTypeLabel(type?: PollType, quick?: boolean): string {
    if (type === PollType.Fast || (type === undefined && quick === true)) return 'Быстрый';
    if (type === PollType.Complex || (type === undefined && quick === false)) return 'Комплексный';
    return 'Опрос';
}

export function isQuickPoll(type?: PollType, quick?: boolean): boolean | undefined {
    if (type !== undefined) return type === PollType.Fast;
    return quick;
}

/** «1 опрос», «2 опроса», «5 опросов». */
export function pluralize(count: number, forms: [string, string, string]): string {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return `${count} ${forms[0]}`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${count} ${forms[1]}`;
    return `${count} ${forms[2]}`;
}

export const pollsCount = (n: number): string => pluralize(n, ['опрос', 'опроса', 'опросов']);
export const questionsCount = (n: number): string => pluralize(n, ['вопрос', 'вопроса', 'вопросов']);
export const answersCount = (n: number): string => pluralize(n, ['ответ', 'ответа', 'ответов']);
